import { ApolloError, useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { getUnprocessableErrors } from '@10xlabs/utils'
import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { IEditBusinessCustomerPlaceFormValues } from 'features/BusinessCustomer/types'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { GTBusinessCustomer } from 'gql/types.generated'
import { REPLACE_PLACE_FOR_BUSINESS_CUSTOMER } from 'features/BusinessCustomer/mutations-legacy'
import { schema } from './schema'

interface IFormProps {
  businessCustomer: GTBusinessCustomer
  onSubmit: (values: IEditBusinessCustomerPlaceFormValues) => void
  onCancel: () => void
  loading?: boolean
  error?: ApolloError
}

const messages = defineMessages({
  latitudePlaceholder: {
    id: 'businessCustomer.editBusinessCustomerPlaceModal.latitude.placeholder',
    defaultMessage: 'Enter latitude'
  },
  longitudePlaceholder: {
    id: 'businessCustomer.editBusinessCustomerPlaceModal.longitude.placeholder',
    defaultMessage: 'Enter longitude'
  },
  streetPlaceholder: {
    id: 'businessCustomer.editBusinessCustomerPlaceModal.street.placeholder',
    defaultMessage: 'Enter street'
  },
  zipCodePlaceholder: {
    id: 'businessCustomer.editBusinessCustomerPlaceModal.zipCode.placeholder',
    defaultMessage: 'Enter zip code'
  },
  cityPlaceholder: {
    id: 'businessCustomer.editBusinessCustomerPlaceModal.city.placeholder',
    defaultMessage: 'Select city'
  }
})

const Form = ({ businessCustomer, onSubmit, onCancel, loading, error }: IFormProps): ReactElement => {
  const intl = useIntl()

  const initialValues: IEditBusinessCustomerPlaceFormValues = {
    street: '',
    zipCode: '',
    city: {
      id: '',
      name: ''
    },
    location: {
      latitude: '',
      longitude: ''
    }
  }
  const userFullName = `${businessCustomer.userAccount.firstName} ${businessCustomer.userAccount.lastName}`

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema(intl)}>
      {({ values, handleSubmit, handleChange, setFieldValue, handleBlur, touched, errors, dirty }): ReactElement => (
        <form onSubmit={handleSubmit}>
          <Modal.Content>
            <Row>
              <Column>
                <Input
                  value={userFullName}
                  label={
                    <FormattedMessage id="businessCustomer.editBusinessCustomerPlaceModal.name" defaultMessage="Name" />
                  }
                  disabled
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Input
                  name="location.latitude"
                  value={values.location.latitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched.location?.latitude}
                  errors={errors.location?.latitude}
                  label={
                    <FormattedMessage
                      id="businessCustomer.editBusinessCustomerPlaceModal.latitude"
                      defaultMessage="Latitude"
                    />
                  }
                  placeholder={intl.formatMessage(messages.latitudePlaceholder)}
                />
              </Column>
              <Column>
                <Input
                  name="location.longitude"
                  value={values.location.longitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched.location?.longitude}
                  errors={errors.location?.longitude}
                  label={
                    <FormattedMessage
                      id="businessCustomer.editBusinessCustomerPlaceModal.longitude"
                      defaultMessage="Longitude"
                    />
                  }
                  placeholder={intl.formatMessage(messages.longitudePlaceholder)}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Input
                  name="street"
                  value={values.street}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched.street}
                  errors={errors.street}
                  label={
                    <FormattedMessage
                      id="businessCustomer.editBusinessCustomerPlaceModal.street"
                      defaultMessage="Street"
                    />
                  }
                  placeholder={intl.formatMessage(messages.streetPlaceholder)}
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Input
                  name="zipCode"
                  value={values.zipCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched.zipCode}
                  errors={errors.zipCode}
                  label={
                    <FormattedMessage
                      id="businessCustomer.editBusinessCustomerPlaceModal.zipCode"
                      defaultMessage="Zip Code"
                    />
                  }
                  placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
                />
              </Column>
              <Column>
                <CityAutocomplete
                  name="city"
                  value={values.city.name}
                  onChange={(city): void => {
                    setFieldValue('city', {
                      id: city?.id,
                      name: city?.name
                    })
                  }}
                  onBlur={handleBlur}
                  touched={touched.city?.id}
                  errors={errors.city?.id}
                  label={
                    <FormattedMessage id="businessCustomer.editBusinessCustomerPlaceModal.city" defaultMessage="City" />
                  }
                  placeholder={intl.formatMessage(messages.cityPlaceholder)}
                />
              </Column>
            </Row>
          </Modal.Content>
          <Modal.Footer>
            {error && (
              <Modal.ErrorMessage>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="businessCustomer.editBusinessCustomerPlaceModal.error"
                    defaultMessage="An error occurred while editing the place for the business customer "
                  />
                </ErrorMessage>
              </Modal.ErrorMessage>
            )}
            <Modal.Actions>
              <Button kind="cancel" type="button" onClick={onCancel}>
                <FormattedMessage
                  id="businessCustomer.editBusinessCustomerPlaceModal.actions.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button type="submit" disabled={!dirty} loading={loading}>
                <FormattedMessage
                  id="businessCustomer.editBusinessCustomerPlaceModal.actions.save"
                  defaultMessage="Save"
                />
              </Button>
            </Modal.Actions>
          </Modal.Footer>
        </form>
      )}
    </Formik>
  )
}

interface IEditBusinessCustomerPlaceModalProps {
  onClose: () => void
  businessCustomer: GTBusinessCustomer
}

export const EditBusinessCustomerPlaceModal = ({
  onClose,
  businessCustomer
}: IEditBusinessCustomerPlaceModalProps): ReactElement => {
  const [replacePlaceForBusinessCustomer, { loading, error }] = useMutation(REPLACE_PLACE_FOR_BUSINESS_CUSTOMER)
  const handleSubmit = async (values: IEditBusinessCustomerPlaceFormValues): Promise<void> => {
    try {
      await replacePlaceForBusinessCustomer({
        variables: {
          input: {
            businessCustomerID: businessCustomer.id,
            businessClientID: businessCustomer.businessClient?.id || null,
            businessSiteID: businessCustomer.businessSite?.id || null,
            name: `${businessCustomer.userAccount.firstName} ${businessCustomer.userAccount.lastName}`,
            currentPlaceID: businessCustomer.place?.id,
            place: {
              address: values.street,
              zipCode: values.zipCode,
              parentID: values.city.id,
              location: {
                latitude: values.location.latitude,
                longitude: values.location.longitude
              }
            }
          }
        }
      })
      onClose()
    } catch (ex) {
      // Do nothing
    }
  }

  return (
    <Modal.Container onClose={onClose}>
      <Modal.Header smallMarginBottom>
        <FormattedMessage id="businessCustomer.editBusinessCustomerPlaceModal.title" defaultMessage="Edit Place" />
      </Modal.Header>
      <Form
        businessCustomer={businessCustomer}
        onSubmit={handleSubmit}
        onCancel={onClose}
        loading={loading}
        error={error}
      />
    </Modal.Container>
  )
}
